<template>
  <CCard>
    <CCardHeader>
      Tunnel List
    </CCardHeader>
    <CCardBody>
      <CRow>
        <CCol sm="12">
          <CButton
            color="success"
            square
            class="mb-3"
            @click="openAddModal"
          >
            Add Tunnel
          </CButton>
          <CDataTable
            hover
            small
            fixed
            table-filter
            sorter
            items-per-page-select
            :items-per-page="15"
            :fields="fields"
            :items="tunnels"
            :noItemsView="{ noItems: 'No Item~' }"
            pagination
          >
            <template #name="{item}">
              <td class="py-2" style="word-break: break-all; font-size: 0.75rem;">
                {{ item.name }}
                <div
                  v-if="item.public_key"
                  style="font-size: 0.7rem; color: #73818f; cursor: pointer;"
                  title="点击复制 WireGuard 公钥"
                  @click="copyPublicKey(item.public_key)"
                >
                  🔑 {{ item.public_key }}
                </div>
              </td>
            </template>
            <template #localRouter="{item}">
              <td class="py-2">
                {{ item.local_router ? item.local_router.name : '-' }}
              </td>
            </template>
            <template #address_family="{item}">
              <td class="py-2">
                <CBadge :color="item.address_family === 'ipv6' ? 'info' : 'secondary'">
                  {{ item.address_family === 'ipv6' ? 'IPv6' : 'IPv4' }}
                </CBadge>
              </td>
            </template>
            <template #remote="{item}">
              <td class="py-2">
                {{ item.remoteLabel }}
                <CBadge
                  v-if="item.remote_endpoint && item.remote_endpoint.owner_type === 'router'"
                  :color="item.peer_existed ? 'success' : 'warning'"
                  style="margin-left: 4px;"
                >
                  {{ item.peer_existed ? 'Peer ✓' : 'No Peer' }}
                </CBadge>
              </td>
            </template>
            <template #enabled="{item}">
              <td class="py-2">
                <CBadge :color="item.enabled ? 'success' : 'danger'">
                  {{ item.enabled ? 'Enabled' : 'Disabled' }}
                </CBadge>
              </td>
            </template>
            <template #action="{item}">
              <td class="py-2">
                <CButtonGroup>
                  <CButton
                    :color="item.enabled ? 'warning' : 'success'"
                    square
                    @click="toggleTunnel(item.id)"
                  >
                    {{ item.enabled ? 'Disable' : 'Enable' }}
                  </CButton>
                  <CButton
                    color="warning"
                    square
                    @click="repushTunnel(item)"
                  >
                    Repush
                  </CButton>
                  <CButton
                    color="primary"
                    square
                    @click="openEditModal(item)"
                  >
                    Edit
                  </CButton>
                  <CButton
                    color="danger"
                    square
                    @click="openDeleteModal(item)"
                  >
                    Delete
                  </CButton>
                </CButtonGroup>
              </td>
            </template>
          </CDataTable>
        </CCol>
      </CRow>
    </CCardBody>
    <CToaster :autohide="3000">
      <template v-for="toast in fixedToasts">
        <CToast
          :key="'toast' + toast"
          :show="true"
          :color="alertLevel"
          header="Alert"
        >
          {{ returnMsg }}
        </CToast>
      </template>
    </CToaster>
    <CModal
      title="Message"
      color="primary"
      :show.sync="requestMsgModal"
    >
      {{ requestMsg }}
      <template #footer>
        <CButton @click="requestMsgModal=false" color="primary">Close</CButton>
      </template>
    </CModal>

    <!-- Add Tunnel -->
    <CModal
      title="Add Tunnel"
      color="success"
      :show.sync="addModal"
      size="lg"
    >
      <CForm>
        <CSelect
          label="Type"
          :options="typeOptions"
          :value.sync="addForm.type"
          @change="onAddTypeChange"
        />
        <CAlert v-if="addForm.type === 'eoip' || addForm.type === 'eoipv6'" color="info" fade>
          EOIP tunnel only supports Router to Router, and a tunnel-id will be allocated automatically.
          eoip 走 IPv4 端点，eoipv6 走 IPv6 端点（RouterOS 7.13+ 的 /interface/eoipv6 菜单）。
        </CAlert>
        <CAlert v-if="addForm.type === 'wireguard'" color="info" fade>
          WireGuard：R→R 自动生成密钥对并互加 peer（listen-port 自动分配于 50000-65535，allowed-address 0.0.0.0/0 + ::/0 全放行，
          peer 命名 peer-to-{region}-{isp}-{nickname}，MTU 默认 1420=出口1480-60）；
          R→CMDB/外部 只下发本端接口，创建后请在列表复制本端公钥到对端配置。
        </CAlert>
        <CSelect
          v-if="addForm.type === 'vxlan'"
          label="VTEP 端口"
          v-model="addForm.vtep_port"
          :options="[
            { value: 'standard', label: '4789（标准端口）' },
            { value: 'random', label: '随机（50000-65535，自动避开 WireGuard）' }
          ]"
          placeholder="4789（标准端口）"
        />
        <CAlert v-if="addForm.type === 'vxlan'" color="info" fade>
          VXLAN：vni 自动分配（两端一致），vteps-ip-version 按所选 endpoint 地址族自动设置（IPv4/IPv6），两端互指 vtep。
        </CAlert>

        <hr class="my-2" />
        <CCardText><strong>Local (本端)</strong></CCardText>
        <div class="form-group">
          <label class="form-label">Local Router</label>
          <SearchSelect :options="routerOptions" v-model="addForm.local_router_id" @change="onAddRouterChange" />
        </div>
        <div class="form-group">
          <label class="form-label">Local Endpoint</label>
          <SearchSelect :options="localEndpointOptions" v-model="addForm.local_endpoint_id" />
        </div>
        <div class="form-group">
          <label class="form-label">Local Address（隧道源地址，已纳管 IP，可选 = auto）</label>
          <SearchSelect :options="localManagedIpOptions" v-model="addForm.local_address" />
          <small class="text-muted">不设置则由 RouterOS 自动选择源地址；只能选择本端路由器已纳管的地址。</small>
        </div>

        <hr class="my-2" />
        <CCardText><strong>Remote (远端)</strong></CCardText>
        <CSelect
          label="Remote Endpoint Type"
          :options="remoteTypeOptions"
          :value.sync="addForm.remote_owner_type"
          @change="onRemoteTypeChange"
        />
        <div class="form-group" v-if="addForm.remote_owner_type === 'router'">
          <label class="form-label">Remote Router</label>
          <SearchSelect :options="remoteRouterOptions" v-model="addForm.remote_router_id" @change="onRemoteInstanceChange" />
        </div>
        <div class="form-group" v-if="addForm.remote_owner_type === 'cmdb'">
          <label class="form-label">Remote CMDB Device</label>
          <SearchSelect :options="remoteDeviceOptions" v-model="addForm.remote_cmdb_id" @change="onRemoteInstanceChange" />
        </div>
        <div class="form-group">
          <label class="form-label">Remote Endpoint</label>
          <SearchSelect :options="remoteEndpointOptions" v-model="addForm.remote_endpoint_id" />
        </div>
        <div class="form-group" v-if="isRouterToRouter">
          <label class="form-label">对端 Local Address（隧道源地址，已纳管 IP，可选 = auto）</label>
          <SearchSelect :options="remoteManagedIpOptions" v-model="addForm.remote_local_address" />
          <small class="text-muted">不设置则对端由 RouterOS 自动选择源地址；只能选择对端路由器已纳管的地址。</small>
        </div>

        <hr class="my-2" />
        <CCardText><strong>Parameters</strong></CCardText>
        <div class="form-group" v-if="addForm.remote_owner_type !== 'external'">
          <label class="form-label">互联 IP（Link IP 分配，必选）</label>
          <CSelect
            :options="matchedAllocationOptions"
            :value.sync="addForm.link_ip_allocation_id"
            placeholder="选择两端匹配的已分配互联 IP"
          />
          <small v-if="selectedAllocation" class="text-muted">
            本端 {{ allocLocalIp }} / 对端 {{ allocRemoteIp }}
            <span v-if="selectedAllocation.remark">（{{ selectedAllocation.remark }}）</span>
          </small>
          <CAlert v-if="matchedAllocationOptions.length === 0" color="warning" fade class="mt-1 mb-0">
            本端与对端之间没有匹配的 Link IP 分配记录，请先到「Link IPs」页面分配。
          </CAlert>
        </div>
        <CInput
          v-if="addForm.remote_owner_type === 'external'"
          label="Local Link Address (互联地址, 可选)"
          v-model="addForm.local_link_address"
          placeholder="如 172.16.255.1/30 (不带前缀默认 /30 或 /126)"
        />
        <CAlert v-if="isRouterToRouter" color="info" fade>
          Router→Router：创建时两端同时下发，生成两条独立记录（各自 uuid/编辑/删除，互不影响）。
        </CAlert>
        <CInput
          label="MTU"
          v-model="addForm.mtu"
          placeholder="auto"
        />
        <CInput
          v-if="!['6to4','wireguard','vxlan'].includes(addForm.type)"
          label="Keepalive"
          v-model="addForm.keepalive"
          placeholder="e.g. 10s,10"
        />
        <CInput
          v-if="!['6to4','wireguard','vxlan'].includes(addForm.type)"
          label="IPSec Secret"
          v-model="addForm.ipsec_secret"
          placeholder="optional"
        />
        <CInputCheckbox
          label="Clamp TCP MSS"
          :checked.sync="addForm.clamp_tcp_mss"
          inline
        />
        <CInputCheckbox
          v-if="!['6to4','wireguard','vxlan'].includes(addForm.type)"
          label="Allow Fast Path"
          :checked.sync="addForm.allow_fast_path"
          inline
        />
        <CInput
          label="Remark"
          v-model="addForm.remark"
        />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="addModal=false">Cancel</CButton>
          <CButton color="success" @click="addTunnel">Create</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- Edit Tunnel -->
    <CModal
      title="Edit Tunnel"
      color="primary"
      :show.sync="editModal"
      size="lg"
    >
      <CForm>
        <CInput label="Type" :value="editForm.type" disabled />
        <CInput label="Local Address (by endpoint)" :value="editForm.local_address || 'auto'" disabled />
        <CInput label="Remote Address (by endpoint)" :value="editForm.remote_address" disabled />
        <CInput
          label="Local Link Address (留空=移除)"
          v-model="editForm.local_link_address"
          placeholder="如 172.16.255.1/30, 留空移除"
        />
        <CInput
          label="MTU"
          v-model="editForm.mtu"
          placeholder="auto"
        />
        <CInput
          v-if="!['6to4','wireguard','vxlan'].includes(editForm.type)"
          label="Keepalive"
          v-model="editForm.keepalive"
          placeholder="e.g. 10s,10"
        />
        <CInput
          v-if="!['6to4','wireguard','vxlan'].includes(editForm.type)"
          label="IPSec Secret"
          v-model="editForm.ipsec_secret"
          placeholder="optional"
        />
        <CInputCheckbox
          label="Clamp TCP MSS"
          :checked.sync="editForm.clamp_tcp_mss"
          inline
        />
        <CInputCheckbox
          v-if="!['6to4','wireguard','vxlan'].includes(editForm.type)"
          label="Allow Fast Path"
          :checked.sync="editForm.allow_fast_path"
          inline
        />
        <CInput
          label="Remark"
          v-model="editForm.remark"
        />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="editModal=false">Cancel</CButton>
          <CButton color="success" @click="editTunnel">Save</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- Delete Confirm -->
    <CModal
      title="Delete Tunnel"
      color="danger"
      :show.sync="deleteModal"
    >
      <CAlert color="danger" fade>
        确定要删除隧道「{{ deleteTarget.name }}」吗？该隧道将从路由器（含远端）上移除，此操作不可恢复。
      </CAlert>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="deleteModal=false">Cancel</CButton>
          <CButton color="danger" @click="deleteTunnel">Delete</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </CCard>
</template>

<script>
const fields = [
  { key: "id", label: "ID" },
  { key: "name", label: "Name" },
  { key: "type", label: "Type" },
  { key: "address_family", label: "Family" },
  { key: "localRouter", label: "Local Router" },
  { key: "local_address", label: "Local Addr" },
  { key: "remote", label: "Remote" },
  { key: "remote_address", label: "Remote Addr" },
  { key: "tunnel_id", label: "Tunnel ID" },
  { key: "enabled", label: "Status" },
  { key: "remark", label: "Remark" },
  {
    key: "action",
    label: "Action",
    sorter: false,
    filter: false,
    _style: "width: 18%"
  }
];

const TYPE_FAMILY = {
  gre: "ipv4",
  gre6: "ipv6",
  eoip: "ipv4",
  eoipv6: "ipv6",
  ipip: "ipv4",
  ipipv6: "ipv6",
  "6to4": "ipv4",
  wireguard: "ipv4",
  vxlan: null // 双栈: vteps-ip-version 由 endpoint 地址族决定
};

export default {
  name: "TunnelList",
  data() {
    return {
      fixedToasts: 0,
      returnMsg: "",
      alertLevel: "success",
      requestMsgModal: false,
      fields,
      tunnels: [],
      routers: [],
      devices: [],
      endpoints: [],
      linkIpAllocations: [],
      localManagedIps: [],
      remoteManagedIps: [],
      addModal: false,
      editModal: false,
      deleteModal: false,
      deleteTarget: { id: null, name: '' },
      typeOptions: [
        { value: "gre", label: "GRE (IPv4)" },
        { value: "gre6", label: "GRE (IPv6)" },
        { value: "eoip", label: "EOIP (IPv4)" },
        { value: "eoipv6", label: "EOIP (IPv6)" },
        { value: "ipip", label: "IPIP (IPv4)" },
        { value: "ipipv6", label: "IPIP (IPv6)" },
        { value: "6to4", label: "6to4" },
        { value: "wireguard", label: "WireGuard" },
        { value: "vxlan", label: "VXLAN" }
      ],
      remoteTypeOptions: [
        { value: "router", label: "Router" },
        { value: "cmdb", label: "CMDB Device" },
        { value: "external", label: "External" }
      ],
      addForm: this.emptyAddForm(),
      editForm: {
        id: null,
        type: '',
        local_address: '',
        remote_address: '',
        local_link_address: '',
        remote_link_address: '',
        local_link_subnet: '',
        isRouterToRouter: false,
        mtu: '',
        keepalive: '',
        ipsec_secret: '',
        clamp_tcp_mss: true,
        allow_fast_path: true,
        remark: ''
      }
    };
  },
  computed: {
    currentFamily: function() {
      const f = TYPE_FAMILY[this.addForm.type];
      return f === undefined ? "ipv4" : f; // eoip=null → 双栈, 不过滤
    },
    isRouterToRouter: function() {
      return this.addForm.remote_owner_type === "router";
    },
    routerOptions: function() {
      let options = [];
      this.routers.map(item => {
        options.push({ value: item.id, label: item.name + "(" + item.host + ")" });
      });
      return options;
    },
    remoteRouterOptions: function() {
      let options = [];
      this.routers.map(item => {
        options.push({ value: item.id, label: item.name + "(" + item.host + ")" });
      });
      return options;
    },
    remoteDeviceOptions: function() {
      let options = [];
      this.devices.map(item => {
        options.push({ value: item.id, label: item.name + "(#" + item.id + ")" });
      });
      return options;
    },
    localEndpointOptions: function() {
      let options = [{ value: 0, label: "None (auto)" }];
      this.endpoints.map(ep => {
        if (ep.owner_type === "router" && ep.router_id == this.addForm.local_router_id
            && (!this.currentFamily || ep.address_family === this.currentFamily)) {
          options.push({ value: ep.id, label: ep.address + (ep.remark ? " - " + ep.remark : "") });
        }
      });
      return options;
    },
    localManagedIpOptions: function() {
      // 本端已纳管 IP（按隧道类型地址族过滤; vxlan 双栈不过滤）
      const fam = TYPE_FAMILY[this.addForm.type];
      return this.localManagedIps
        .filter(i => !fam || i.family === fam || i.family === '')
        .map(i => {
          const ip = (i.address || '').split('/')[0];
          return { value: ip, label: `${ip} (${i.interface_name})` };
        });
    },
    remoteManagedIpOptions: function() {
      const fam = TYPE_FAMILY[this.addForm.type];
      return this.remoteManagedIps
        .filter(i => !fam || i.family === fam || i.family === '')
        .map(i => {
          const ip = (i.address || '').split('/')[0];
          return { value: ip, label: `${ip} (${i.interface_name})` };
        });
    },
    remoteEndpointOptions: function() {
      let options = [];
      this.endpoints.map(ep => {
        if (this.currentFamily && ep.address_family !== this.currentFamily) return;
        if (this.addForm.remote_owner_type === "router") {
          if (ep.owner_type === "router" && ep.router_id == this.addForm.remote_router_id) {
            options.push({ value: ep.id, label: ep.address + (ep.remark ? " - " + ep.remark : "") });
          }
        } else if (this.addForm.remote_owner_type === "cmdb") {
          if (ep.owner_type === "cmdb" && ep.cmdb_id == this.addForm.remote_cmdb_id) {
            options.push({ value: ep.id, label: ep.address + (ep.remark ? " - " + ep.remark : "") });
          }
        } else {
          if (ep.owner_type === "external") {
            options.push({ value: ep.id, label: ep.address + (ep.remark ? " - " + ep.remark : "") });
          }
        }
      });
      return options;
    },
    matchedAllocationOptions: function() {
      const localId = this.addForm.local_router_id;
      const rt = this.addForm.remote_owner_type;
      if (!localId || (rt !== "router" && rt !== "cmdb")) return [];
      const remoteId = rt === "router" ? this.addForm.remote_router_id : this.addForm.remote_cmdb_id;
      if (!remoteId) return [];
      // 隧道内层互联地址 v4/v6 均可（v6 传输的隧道内层照样跑 v4），不做族过滤
      return this.linkIpAllocations
        .filter(al => {
          const a = { type: al.owner_a_type, id: al.router_a_id };
          const b = { type: al.owner_b_type, id: al.router_b_id };
          const localIsA = a.type === "router" && a.id == localId;
          const localIsB = b.type === "router" && b.id == localId;
          const remoteIsA = a.type === rt && a.id == remoteId;
          const remoteIsB = b.type === rt && b.id == remoteId;
          return (localIsA && remoteIsB) || (localIsB && remoteIsA);
        })
        .map(al => ({
          value: al.id,
          label: `${al.cidr}  [${al.owner_a_name || '-'} ${al.a_ip} / ${al.owner_b_name || '-'} ${al.b_ip}]`
        }));
    },
    selectedAllocation: function() {
      if (!this.addForm.link_ip_allocation_id) return null;
      return this.linkIpAllocations.find(al => al.id == this.addForm.link_ip_allocation_id) || null;
    },
    allocLocalIp: function() {
      const al = this.selectedAllocation;
      if (!al) return "-";
      const isA = al.owner_a_type === "router" && al.router_a_id == this.addForm.local_router_id;
      return isA ? al.a_ip : al.b_ip;
    },
    allocRemoteIp: function() {
      const al = this.selectedAllocation;
      if (!al) return "-";
      const isA = al.owner_a_type === "router" && al.router_a_id == this.addForm.local_router_id;
      return isA ? al.b_ip : al.a_ip;
    }
  },
  mounted: function() {
    this.onLoadPage();
  },
  methods: {
    emptyAddForm() {
      return {
        type: "gre",
        local_router_id: null,
        local_endpoint_id: null,
        remote_owner_type: "router",
        remote_router_id: null,
        remote_cmdb_id: null,
        remote_endpoint_id: null,
        local_address: "",
        remote_local_address: "",
        link_ip_allocation_id: null,
        local_link_address: "",
        remote_link_address: "",
        vtep_port: "standard",
        mtu: "",
        keepalive: "",
        ipsec_secret: "",
        clamp_tcp_mss: true,
        allow_fast_path: true,
        remark: ""
      };
    },
    copyPublicKey: function(pk) {
      const el = document.createElement("textarea");
      el.value = pk;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      this.alertLevel = "success";
      this.returnMsg = "公钥已复制";
      this.fixedToasts++;
    },
    endpointLabel: function(ep) {
      if (ep.owner_type === "router") {
        return "[Router] " + (ep.router ? ep.router.name : "#" + ep.router_id) + " - " + ep.address;
      }
      if (ep.owner_type === "cmdb") {
        return "[CMDB] " + (ep.cmdb ? ep.cmdb.name : "#" + ep.cmdb_id) + " - " + ep.address;
      }
      return "[External] " + ep.address;
    },
    onLoadPage: function() {
      this.$axios.get("user/tunnel/list", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.tunnels = response.data.tunnels.map(item => {
              let remoteLabel = "-";
              if (item.remote_endpoint) {
                remoteLabel = this.endpointLabel(item.remote_endpoint);
              } else if (item.remote_display) {
                remoteLabel = item.remote_display;
              }
              return { ...item, remoteLabel: remoteLabel };
            });
          }
        });

      this.$axios.get("user/router", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.routers = response.data.userRouter;
          }
        });

      this.$axios.get("user/cmdb/device/list", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.devices = response.data.devices;
          }
        });

      this.$axios.get("user/tunnel/endpoint/list", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.endpoints = response.data.endpoints;
          }
        });

      this.$axios.get("user/link-ip/list", {})
        .then((response) => {
          if (response.data.code === 200) {
            const list = [];
            (response.data.data || []).forEach(pool => {
              (pool.allocations || []).forEach(al => {
                list.push({ ...al, pool_network: pool.network });
              });
            });
            this.linkIpAllocations = list;
          }
        });
    },
    onAddTypeChange: function() {
      // 切换类型后地址族可能变化, 清空不匹配的端点选择（双栈类型如 eoip 不清）
      let family = TYPE_FAMILY[this.addForm.type];
      if (family === undefined) family = "ipv4";
      if (family === null) return;
      let localEp = this.endpoints.find(e => e.id == this.addForm.local_endpoint_id);
      if (localEp !== undefined && localEp.address_family !== family) {
        this.addForm.local_endpoint_id = null;
      }
      let remoteEp = this.endpoints.find(e => e.id == this.addForm.remote_endpoint_id);
      if (remoteEp !== undefined && remoteEp.address_family !== family) {
        this.addForm.remote_endpoint_id = null;
      }
      // 已纳管 IP 源地址同理按族清空
      if (family !== null) {
        const la = (this.addForm.local_address || '').split('/')[0];
        if (la && !la.includes(':') !== (family === 'ipv4')) {
          this.addForm.local_address = "";
        }
        const ra = (this.addForm.remote_local_address || '').split('/')[0];
        if (ra && !ra.includes(':') !== (family === 'ipv4')) {
          this.addForm.remote_local_address = "";
        }
      }
    },
    onAddRouterChange: function() {
      this.addForm.local_endpoint_id = null;
      this.addForm.local_address = "";
      this.addForm.link_ip_allocation_id = null;
      this.loadManagedIps('local', this.addForm.local_router_id);
    },
    onRemoteTypeChange: function() {
      this.addForm.remote_router_id = null;
      this.addForm.remote_cmdb_id = null;
      this.addForm.remote_endpoint_id = null;
      this.addForm.remote_local_address = "";
      this.remoteManagedIps = [];
      this.addForm.link_ip_allocation_id = null;
    },
    onRemoteInstanceChange: function() {
      this.addForm.remote_endpoint_id = null;
      this.addForm.remote_local_address = "";
      this.addForm.link_ip_allocation_id = null;
      const rid = this.addForm.remote_owner_type === "router" ? this.addForm.remote_router_id : this.addForm.remote_cmdb_id;
      if (this.addForm.remote_owner_type === "router" && rid) {
        this.loadManagedIps('remote', rid);
      } else {
        this.remoteManagedIps = [];
      }
    },
    async loadManagedIps(which, routerId) {
      const target = which === 'local' ? 'localManagedIps' : 'remoteManagedIps';
      if (!routerId) {
        this[target] = [];
        return;
      }
      try {
        const res = await this.$axios.get('user/ip-address/list', { params: { router_id: routerId } });
        if (res.data.code === 200) {
          this[target] = res.data.ip_addresses || [];
        }
      } catch (e) {
        this[target] = [];
      }
    },
    openAddModal: function() {
      this.addForm = this.emptyAddForm();
      this.addModal = true;
    },
    addTunnel: function() {
      if (!this.addForm.local_router_id) {
        this.requestMsg = "Please select local router";
        this.requestMsgModal = true;
        return;
      }
      if ((this.addForm.type === "eoip" || this.addForm.type === "eoipv6") && this.addForm.remote_owner_type !== "router") {
        this.requestMsg = "EOIP tunnel only supports Router to Router";
        this.requestMsgModal = true;
        return;
      }
      if (this.addForm.remote_owner_type === "router" && !this.addForm.remote_router_id) {
        this.requestMsg = "Please select remote router";
        this.requestMsgModal = true;
        return;
      }
      if (this.addForm.remote_owner_type === "cmdb" && !this.addForm.remote_cmdb_id) {
        this.requestMsg = "Please select remote CMDB device";
        this.requestMsgModal = true;
        return;
      }
      if (!this.addForm.remote_endpoint_id) {
        this.requestMsg = "Please select remote endpoint";
        this.requestMsgModal = true;
        return;
      }
      if (this.addForm.remote_owner_type !== "external" && !this.addForm.link_ip_allocation_id) {
        this.requestMsg = "Router/CMDB endpoint requires a Link IP allocation (please allocate in Link IPs page first)";
        this.requestMsgModal = true;
        return;
      }
      this.$axios.post("user/tunnel/add", {
        type: this.addForm.type,
        local_router_id: this.addForm.local_router_id,
        local_endpoint_id: this.addForm.local_endpoint_id || null,
        local_address: this.addForm.local_address || "",
        remote_local_address: this.addForm.remote_local_address || "",
        remote_endpoint_id: this.addForm.remote_endpoint_id,
        link_ip_allocation_id: this.addForm.remote_owner_type !== "external"
          ? this.addForm.link_ip_allocation_id : null,
        local_link_address: this.addForm.local_link_address,
        remote_link_address: this.addForm.remote_link_address,
        vtep_port: this.addForm.type === "vxlan" ? this.addForm.vtep_port : null,

        mtu: this.addForm.mtu,
        keepalive: this.addForm.keepalive,
        ipsec_secret: this.addForm.ipsec_secret,
        clamp_tcp_mss: this.addForm.clamp_tcp_mss ? 1 : 0,
        allow_fast_path: this.addForm.allow_fast_path ? 1 : 0,
        remark: this.addForm.remark
      })
        .then((response) => {
          if (response.data.code === 200) {
            this.addModal = false;
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.onLoadPage();
          } else {
            this.addModal = false;
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
    openEditModal: function(item) {
      this.editForm = {
        id: item.id,
        type: item.type,
        local_address: item.local_address || '',
        remote_address: item.remote_address,
        local_link_address: item.local_link_address || '',
        mtu: item.mtu || '',
        keepalive: item.keepalive || '',
        ipsec_secret: item.ipsec_secret || '',
        clamp_tcp_mss: item.clamp_tcp_mss === null || item.clamp_tcp_mss === undefined ? true : !!item.clamp_tcp_mss,
        allow_fast_path: item.allow_fast_path === null || item.allow_fast_path === undefined ? true : !!item.allow_fast_path,
        remark: item.remark || ''
      };
      this.editModal = true;
    },
    editTunnel: function() {
      this.$axios.post("user/tunnel/" + this.editForm.id + "/edit", {
        local_link_address: this.editForm.local_link_address,
        mtu: this.editForm.mtu,
        keepalive: this.editForm.keepalive,
        ipsec_secret: this.editForm.ipsec_secret,
        clamp_tcp_mss: this.editForm.clamp_tcp_mss ? 1 : 0,
        allow_fast_path: this.editForm.allow_fast_path ? 1 : 0,
        remark: this.editForm.remark
      })
        .then((response) => {
          if (response.data.code === 200) {
            this.editModal = false;
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.onLoadPage();
          } else {
            this.editModal = false;
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
    toggleTunnel: function(id) {
      this.$axios.post("user/tunnel/" + id + "/toggle", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.onLoadPage();
          } else {
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
    repushTunnel: function(item) {
      this.$axios.post("user/tunnel/" + item.id + "/repush", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.onLoadPage();
          } else {
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
    openDeleteModal: function(item) {
      this.deleteTarget = { id: item.id, name: item.name };
      this.deleteModal = true;
    },
    deleteTunnel: function() {
      this.$axios.delete("user/tunnel/" + this.deleteTarget.id + "/delete", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.deleteModal = false;
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.onLoadPage();
          } else {
            this.deleteModal = false;
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
  }
};
</script>
